import { Button, Callout, ErrorState, Skeleton, Text, Toaster, toast } from "@imagedesignai/neo";
import "@imagedesignai/neo/fonts.css";
import "@imagedesignai/neo/styles.css";
import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import { StrictMode, useCallback, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { AdminView } from "./admin-view.tsx";
import type { PublicConfig, ShareLinkView } from "./api-client.ts";
import { createAuthenticatedResources } from "./authenticated-resources.ts";
import { MAX_PICKER_PREVIEWS } from "./constants.ts";
import "./layout.css";
import { LibraryView } from "./library-view.tsx";
import { RunView } from "./run-view.tsx";
import { sharePreparationErrorMessage, tryCopyRecipientLink } from "./share-copy.ts";
import { studioPrefillFromShareSource } from "./share-handoff.ts";
import { ShareRecoveryModal } from "./share-recovery-modal.tsx";
import { type StudioPrefill, StudioView } from "./studio-view.tsx";

type Route = "studio" | "admin" | "library";

function currentRoute(): Route {
  if (location.pathname === "/library") return "library";
  return location.pathname === "/admin" ? "admin" : "studio";
}

function AuthShell({ client }: { client: SupabaseClient }) {
  const [error, setError] = useState<string | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const signIn = async () => {
    setIsSigningIn(true);
    setError(null);
    const result = await client.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${location.origin}${location.pathname}`,
        queryParams: { hd: "beyondlabs.net" },
      },
    });
    if (result.error) {
      setError(result.error.message);
      setIsSigningIn(false);
    }
  };

  return (
    <main className="auth-layout">
      <section className="auth-card" aria-labelledby="auth-title">
        <h1 id="auth-title" className="wordmark">
          GIF Me
        </h1>
        <Text kind="body-relaxed" tone="secondary">
          Put your face into the team&apos;s favourite loops.
        </Text>
        <Button
          intent="primary"
          size="lg"
          icon="log-in"
          type="button"
          loading={isSigningIn}
          onClick={() => void signIn()}
        >
          Sign in with Google
        </Button>
        <Text kind="caption" tone="muted">
          beyondlabs.net accounts only
        </Text>
        {error ? (
          <Callout intent="danger" label="Sign-in failed">
            {error}
          </Callout>
        ) : null}
      </section>
    </main>
  );
}

function AuthenticatedApp({ client, session }: { client: SupabaseClient; session: Session }) {
  const [route, setRoute] = useState<Route>(currentRoute);
  const [prefill, setPrefill] = useState<StudioPrefill | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [shareLinks, setShareLinks] = useState<ShareLinkView[]>([]);
  const [shareRecovery, setShareRecovery] = useState<{
    linkId: string;
    payload: string;
  } | null>(null);
  const [resources] = useState(() =>
    createAuthenticatedResources({
      accessToken: session.access_token,
      maxEntries: MAX_PICKER_PREVIEWS,
    }),
  );
  resources.useAccessToken(session.access_token);
  const { api, mediaCache } = resources;

  useEffect(() => () => mediaCache.clear(), [mediaCache]);

  useEffect(() => {
    const onPopState = () => setRoute(currentRoute());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const refreshShareLinks = useCallback(async () => {
    try {
      setShareLinks(await api.listShareLinks());
    } catch {
      toast.danger("Could not load share links");
    }
  }, [api]);

  useEffect(() => {
    void refreshShareLinks();
  }, [refreshShareLinks]);

  const navigate = useCallback((next: Route) => {
    const path = next === "library" ? "/library" : next === "admin" ? "/admin" : "/";
    if (location.pathname !== path) history.pushState(null, "", path);
    setRoute(next);
  }, []);

  useEffect(() => {
    let isMounted = true;
    void resources
      .consumeShareHandoffOnce()
      .then((result) => {
        if (!isMounted || result.kind === "absent") return;
        if (result.kind === "invalid") {
          toast.danger("This shared GIF is no longer available to recast.");
          return;
        }
        setPrefill(studioPrefillFromShareSource(result.source));
        navigate("studio");
        toast.positive("Source loaded. Choose your selfie to cast yourself.");
      })
      .catch((error) => {
        if (isMounted) console.error("GIF Me share handoff request failed", error);
      });
    return () => {
      isMounted = false;
    };
  }, [navigate, resources]);

  const rememberLink = (link: ShareLinkView) => {
    setShareLinks((current) => [
      link,
      ...current.filter((candidate) => candidate.runId !== link.runId),
    ]);
  };

  const prepareShareOutput = async (runId: string): Promise<ShareLinkView> => {
    const link = await api.createShareLink(runId);
    rememberLink(link);
    return link;
  };

  const shareOutput = async (link: Promise<ShareLinkView>) => {
    try {
      const result = await tryCopyRecipientLink({
        link,
        absoluteUrl: (path) => new URL(path, location.origin).href,
        onCopyFailed: (link, payload) =>
          setShareRecovery({
            linkId: link.id,
            payload,
          }),
        clipboard: navigator.clipboard,
        createClipboardItem:
          typeof ClipboardItem === "undefined"
            ? undefined
            : (representations) => new ClipboardItem(representations),
      });
      if (result.copied) {
        setShareRecovery((current) => (current?.linkId === result.link.id ? null : current));
        toast.positive("Share link copied");
      } else {
        toast.danger("Copy was blocked. Copy the active link from the dialog.");
      }
    } catch (error) {
      toast.danger(sharePreparationErrorMessage(error));
    }
  };

  const copyRecoveryLink = async () => {
    if (!shareRecovery) return;
    try {
      await navigator.clipboard.writeText(shareRecovery.payload);
      setShareRecovery(null);
      toast.positive("Link copied");
    } catch {
      toast.danger("Copy was blocked. Select and copy the link manually.");
    }
  };

  const revokeLink = async (link: ShareLinkView) => {
    try {
      await api.revokeShareLink(link.id);
      setShareLinks((current) => current.filter(({ id }) => id !== link.id));
      setShareRecovery((current) => (current?.linkId === link.id ? null : current));
      toast.positive("Share link revoked");
    } catch {
      toast.danger("Could not revoke share link");
    }
  };

  const download = async (path: string, filename: string) => {
    try {
      const url = URL.createObjectURL(await api.mediaBlob(path));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      anchor.hidden = true;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch {
      toast.danger("Could not download GIF");
    }
  };

  const prefillStudio = (next: StudioPrefill) => {
    setPrefill(next);
    navigate("studio");
  };

  return (
    <>
      <header className="app-header">
        <a
          className="header-wordmark"
          href="/"
          onClick={(event) => {
            event.preventDefault();
            navigate("studio");
          }}
        >
          GIF Me
        </a>
        <nav className="app-nav" aria-label="Primary">
          <Button
            intent={route === "studio" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => navigate("studio")}
          >
            Studio
          </Button>
          <Button
            intent={route === "admin" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => navigate("admin")}
          >
            Admin
          </Button>
          <Button
            intent={route === "library" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => navigate("library")}
          >
            Library
          </Button>
        </nav>
        <Button intent="ghost" size="sm" icon="log-out" onClick={() => void client.auth.signOut()}>
          Sign out
        </Button>
      </header>
      {route === "admin" ? (
        <main className="admin-layout">
          <AdminView api={api} />
        </main>
      ) : route === "studio" ? (
        <main className="studio-layout">
          <StudioView
            api={api}
            mediaCache={mediaCache}
            prefill={prefill}
            onQueued={() => setRefreshVersion((current) => current + 1)}
          />
          <RunView
            api={api}
            currentUserId={session.user.id}
            refreshVersion={refreshVersion}
            shareLinks={shareLinks}
            onPrepareShare={prepareShareOutput}
            onShare={shareOutput}
            onRevokeLink={revokeLink}
            onPrefill={prefillStudio}
            onDownload={download}
          />
        </main>
      ) : (
        <LibraryView
          api={api}
          mediaCache={mediaCache}
          currentUserId={session.user.id}
          shareLinks={shareLinks}
          onPrepareShare={prepareShareOutput}
          onShare={shareOutput}
          onRevokeLink={revokeLink}
          onDownload={download}
        />
      )}
      {shareRecovery ? (
        <ShareRecoveryModal
          payload={shareRecovery.payload}
          onClose={() => setShareRecovery(null)}
          onCopy={copyRecoveryLink}
        />
      ) : null}
      <Toaster label="GIF Me notifications" />
    </>
  );
}

function App() {
  const [client, setClient] = useState<SupabaseClient | null>(null);
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let unsubscribe = () => {};
    void fetch("/api/config")
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return (await response.json()) as PublicConfig;
      })
      .then(async (config) => {
        if (!isMounted) return;
        const nextClient = createClient(config.supabaseUrl, config.supabasePublishableKey);
        setClient(nextClient);
        const initial = await nextClient.auth.getSession();
        if (!isMounted) return;
        setSession(initial.data.session);
        const listener = nextClient.auth.onAuthStateChange((_event, nextSession) => {
          setSession(nextSession);
        });
        unsubscribe = () => listener.data.subscription.unsubscribe();
      })
      .catch((reason) => {
        if (isMounted) setBootstrapError(reason instanceof Error ? reason.message : String(reason));
      });
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  if (bootstrapError) {
    return (
      <main className="auth-layout">
        <ErrorState title="GIF Me could not start" description={bootstrapError} />
      </main>
    );
  }
  if (!client || session === undefined) {
    return (
      <main className="auth-layout">
        <div className="bootstrap-skeleton">
          <Skeleton arrangement="card" label="Loading GIF Me" />
        </div>
      </main>
    );
  }
  return session ? (
    <AuthenticatedApp key={session.user.id} client={client} session={session} />
  ) : (
    <AuthShell client={client} />
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("GIF Me root element is missing");

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
